import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MessagingPlatformSelector } from "@/components/messaging/MessagingPlatformSelector";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Shield } from "lucide-react";

interface ExternalContactFormProps {
  phoneNumber: string | null;
  whatsappEnabled: boolean;
  telegramUsername: string | null;
  telegramEnabled: boolean;
  preferredPlatform: string;
  onUpdate: () => void;
}

export const ExternalContactForm = ({
  phoneNumber: initialPhone,
  whatsappEnabled: initialWhatsapp,
  telegramUsername: initialTelegram,
  telegramEnabled: initialTelegramEnabled,
  preferredPlatform: initialPlatform,
  onUpdate,
}: ExternalContactFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [phoneNumber, setPhoneNumber] = useState(initialPhone || "");
  const [whatsappEnabled, setWhatsappEnabled] = useState(initialWhatsapp);
  const [telegramUsername, setTelegramUsername] = useState(initialTelegram || "");
  const [telegramEnabled, setTelegramEnabled] = useState(initialTelegramEnabled);
  const [preferredPlatform, setPreferredPlatform] = useState(initialPlatform);
  const [loading, setLoading] = useState(false);

  // Synchroniser les états avec les props quand elles changent
  useEffect(() => {
    setPhoneNumber(initialPhone || "");
    setWhatsappEnabled(initialWhatsapp);
    setTelegramUsername(initialTelegram || "");
    setTelegramEnabled(initialTelegramEnabled);
    setPreferredPlatform(initialPlatform);
  }, [initialPhone, initialWhatsapp, initialTelegram, initialTelegramEnabled, initialPlatform]);

  const validateTelegramUsername = (username: string): boolean => {
    if (!username) return true;
    const cleanUsername = username.startsWith("@") ? username.slice(1) : username;
    const regex = /^[a-zA-Z0-9_]{5,32}$/;
    return regex.test(cleanUsername);
  };

  const handleSave = async () => {
    // Validation
    if (telegramUsername && !validateTelegramUsername(telegramUsername)) {
      toast({
        title: t("contact.error.title"),
        description: t("contact.telegram.validationError"),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error(t("contact.error.notAuthenticated"));

      // Formater le username Telegram avec @
      const formattedTelegram = telegramUsername && !telegramUsername.startsWith("@") 
        ? `@${telegramUsername}` 
        : telegramUsername;

      console.log("[CONTACT SAVE] Saving contact info for user:", user.id);
      console.log("[CONTACT SAVE] Values:", {
        whatsapp_enabled: whatsappEnabled,
        telegram_enabled: telegramEnabled,
        preferred_messaging_platform: preferredPlatform
      });

      const { error } = await supabase
        .from("profiles")
        .update({
          phone_number: phoneNumber || null,
          whatsapp_enabled: whatsappEnabled,
          telegram_username: formattedTelegram || null,
          telegram_enabled: telegramEnabled,
          preferred_messaging_platform: preferredPlatform,
        })
        .eq("id", user.id);

      if (error) {
        console.error("[CONTACT SAVE] Error:", error);
        throw error;
      }

      console.log("[CONTACT SAVE] Success!");

      toast({
        title: t("contact.success.title"),
        description: t("contact.success.description"),
      });

      onUpdate();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast({
        title: t("contact.error.title"),
        description: t("contact.error.updateFailed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 p-4 bg-muted rounded-lg">
        <Shield className="h-5 w-5 text-muted-foreground mt-0.5" />
        <p className="text-sm text-muted-foreground">
          {t("contact.privacyNotice")}
        </p>
      </div>

      {/* WhatsApp Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="whatsapp-enabled" className="text-base font-semibold">
            {t("contact.whatsapp.label")}
          </Label>
          <Switch
            id="whatsapp-enabled"
            checked={whatsappEnabled}
            onCheckedChange={setWhatsappEnabled}
          />
        </div>
        
        {whatsappEnabled && (
          <div className="space-y-2">
            <Label htmlFor="phone-number">{t("contact.whatsapp.phoneLabel")}</Label>
            <Input
              id="phone-number"
              type="tel"
              placeholder="+33612345678"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("contact.whatsapp.phoneFormat")}
            </p>
          </div>
        )}
      </div>

      {/* Telegram Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="telegram-enabled" className="text-base font-semibold">
            {t("contact.telegram.label")}
          </Label>
          <Switch
            id="telegram-enabled"
            checked={telegramEnabled}
            onCheckedChange={setTelegramEnabled}
          />
        </div>
        
        {telegramEnabled && (
          <div className="space-y-2">
            <Label htmlFor="telegram-username">{t("contact.telegram.usernameLabel")}</Label>
            <Input
              id="telegram-username"
              type="text"
              placeholder="@username"
              value={telegramUsername}
              onChange={(e) => setTelegramUsername(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("contact.telegram.usernameHint")}
            </p>
          </div>
        )}
      </div>

      {/* Platform Preference */}
      {(whatsappEnabled || telegramEnabled) && (
        <MessagingPlatformSelector
          value={preferredPlatform}
          onChange={setPreferredPlatform}
        />
      )}

      <Button onClick={handleSave} disabled={loading} className="w-full">
        {loading ? t("contact.saving") : t("contact.saveButton")}
      </Button>
    </div>
  );
};